import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Play, 
  Menu, 
  X, 
  ArrowRight, 
  ChevronDown,
  Mail,
  Phone,
  Instagram,
  Quote
} from "lucide-react";
import ProjectPage from "@/pages/ProjectPage";

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { name: 'Work', href: '#projects' },
    { name: 'Services', href: '#services' },
    { name: 'Marketing', href: '#marketing' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' }
  ];

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a 
              href="#" 
              data-testid="logo"
              className="font-display text-2xl tracking-tight text-white hover:text-[#FFB703] transition-colors"
            >
              JACK PEPPER
            </a>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className="text-sm font-mono uppercase tracking-wider text-zinc-400 hover:text-[#FFB703] transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="nav-cta"
                className="btn-shine px-6 py-2 bg-[#FFB703] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#EAA015] transition-colors"
              >
                Let's Talk
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-white p-2"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-black pt-24 md:hidden"
          >
            <div className="flex flex-col items-center gap-8 p-8">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setIsOpen(false)}
                  className="font-display text-4xl text-white hover:text-[#FFB703] transition-colors"
                >
                  {link.name}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => setIsOpen(false)}
                className="mt-8 px-10 py-4 bg-[#FFB703] text-black font-bold text-lg"
              >
                Let's Talk
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const Hero = () => {
  const [showReel, setShowReel] = useState(false);

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1741296045142-QK9QNP7UTGRUQ0EHQPUH/Film+Hawaii+2-82.jpg?format=2500w"
          alt="Cinematic background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 gradient-vignette" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703] mb-6"
        >
          Denver-Based Multimedia Producer
        </motion.p>
        
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-none tracking-tight mb-8"
        >
          Stories Worth
          <br />
          <span className="text-[#FFB703]">Watching</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="max-w-2xl mx-auto text-lg md:text-xl text-zinc-300 font-light mb-12"
        >
          Story-first approach to visual work. Clean execution. Strong visuals. 
          Content that holds up in the real world.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <button
            data-testid="play-reel-btn"
            onClick={() => setShowReel(true)}
            className="group flex items-center gap-3 px-8 py-4 border border-white/30 hover:border-[#FFB703] hover:bg-[#FFB703]/10 transition-all duration-300"
          >
            <Play size={20} className="text-[#FFB703]" />
            <span className="font-semibold uppercase tracking-wider">Watch Reel</span>
          </button>
          <a
            href="#projects"
            data-testid="view-work-btn"
            className="btn-shine px-8 py-4 bg-[#FFB703] text-black font-bold uppercase tracking-wider hover:bg-[#EAA015] transition-colors"
          >
            View Work
          </a>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="font-mono text-xs uppercase tracking-wider text-zinc-500">Scroll</span>
            <ChevronDown size={20} className="text-zinc-500 animate-scroll-bounce" />
          </div>
        </motion.div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showReel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="video-modal"
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-12"
            onClick={() => setShowReel(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="close-modal-btn"
                onClick={() => setShowReel(false)}
                className="absolute -top-12 right-0 text-white hover:text-[#FFB703] transition-colors"
              >
                <X size={32} />
              </button>
              <div className="video-container">
                <iframe
                  src="https://player.vimeo.com/video/755279690?autoplay=1&title=0&byline=0&portrait=0"
                  title="Jack Pepper Visuals Reel"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="rounded-lg"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Projects Section
const Projects = () => {
  const projects = [
    {
      id: 1,
      title: 'JPV Reel',
      category: 'Showreel',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1723487821663-R218MCAIJREOURSTAGQJ/JPV+Cover+.png?format=1500w',
      span: 'md:col-span-8',
      slug: 'jpv-reel'
    },
    {
      id: 6,
      title: 'Social Media',
      category: 'Content',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1741295068570-EKY0HV2MSH56BYT7TG5I/Socials+Export+2.png?format=1000w',
      span: 'md:col-span-4',
      slug: 'social-media'
    },
    {
      id: 2,
      title: 'Jiberish Lookbooks',
      category: 'Fashion',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/0a97499a-6bab-41a1-b5cc-5f27ba0e5c03/image-asset.jpeg?format=1000w',
      span: 'md:col-span-4',
      slug: 'jiberish-lookbooks'
    },
    {
      id: 4,
      title: 'Oliver Hawaii',
      category: 'Documentary',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1767742565984-CUL1DAJOP5YP03LDV3YQ/Oliver%2BHeader.png?format=1500w',
      span: 'md:col-span-8',
      slug: 'oliver-hawaii'
    },
    {
      id: 3,
      title: 'Strictly',
      category: 'Ski Film',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/18769147-505e-4eae-a20b-10a37a3ced5d/Screen%2BShot%2B2019-08-21%2Bat%2B1.21.10%2BPM.png?format=1000w',
      span: 'md:col-span-6',
      slug: 'strictly'
    },
    {
      id: 5,
      title: 'Fusion Specialties',
      category: 'Commercial',
      image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1765992420635-JM2T2DJY997DS7F0Q0MB/Botanic+Wide+Cover.png?format=1500w',
      span: 'md:col-span-6',
      slug: 'fusion'
    }
  ];

  return (
    <section id="projects" data-testid="projects-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">Selected Work</p>
              <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
                Projects
              </h2>
            </div>
            <a 
              href="#" 
              data-testid="view-all-projects"
              className="hidden"
            >
              <span className="font-mono text-sm uppercase tracking-wider">View All</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {projects.map((project, index) => (
            <AnimatedSection key={project.id} className={`${project.span} project-card group cursor-pointer`}>
              <a 
                href={`/project/${project.slug}`}
                data-testid={`project-${project.id}-link`}
                className="block relative overflow-hidden"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  data-testid={`project-${project.id}-image`}
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-mono text-xs uppercase tracking-wider text-[#FFB703] mb-2">
                    {project.category}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight">
                    {project.title}
                  </h3>
                </div>
              </a>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Brand Collaborations Marquee
const BrandMarquee = () => {
  const brands = [
    'JIBERISH', 'VAN DEER', 'ARBY\'S', 'BANK OF AMERICA', 'ISSEY MIYAKE',
    'SKULLCANDY', 'STONE ISLAND', 'KAPPA', 'HUMAN MADE', 'FIREBALL',
    'PICTURE ORGANIC', 'BREEZY GOLF', 'YY NATION', 'PULLIN', 'FUBUKI'
  ];

  return (
    <section data-testid="brands-section" className="py-16 border-y border-white/10 overflow-hidden marquee-container">
      <Marquee speed={50} gradient={false}>
        {brands.map((brand) => (
          <span
            key={brand}
            className="font-display text-4xl md:text-6xl uppercase mx-8 md:mx-16 text-stroke hover:text-white transition-all duration-300 cursor-default"
          >
            {brand}
          </span>
        ))}
      </Marquee>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      id: 1,
      title: 'Video Production',
      description: 'Full-service video production from concept to final delivery. Brand films, commercials, documentaries, and social content.',
      features: ['Creative Direction', 'Cinematography', 'Editing & Color', 'Sound Design']
    },
    {
      id: 2,
      title: 'Content Strategy',
      description: 'Strategic content planning for brands that want to stand out. Social-first thinking with platform-native execution.',
      features: ['Content Calendar', 'Platform Strategy', 'Performance Analytics', 'Trend Research']
    },
    {
      id: 3,
      title: 'Social Media Marketing',
      description: 'Social-first video content designed to perform. From high-speed editorial cuts to lo-fi UGC-style vertical video built for engagement, reach, and real results.',
      features: ['Short-Form Video', 'UGC & Reels', 'Brand Campaigns', 'Community Growth']
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="text-center mb-16 md:mb-24">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">What I Offer</p>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight mb-6">
              Services
            </h2>
            <p className="max-w-2xl mx-auto text-zinc-400 text-lg">
              Story before polish. Clean execution. Strong visuals.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <AnimatedSection key={service.id}>
              <div 
                data-testid={`service-${service.id}`}
                className="service-card h-full p-8 border border-white/10 hover:border-[#FFB703] transition-all duration-500"
              >
                <span className="font-mono text-xs text-[#FFB703]">{`0${service.id}`}</span>
                <h3 className="font-display text-2xl uppercase tracking-tight mt-4 mb-4">
                  {service.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-zinc-300">
                      <span className="w-1 h-1 bg-[#FFB703]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 border-t border-white/10">
                  <p className="text-sm text-zinc-400">Reach out for rate info</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Marketing Background Section
const MarketingBackground = () => {
  const stats = [
    { value: '10+', label: 'Years in Content & Marketing' },
    { value: '30%', label: 'Engagement Increase' },
    { value: '20%', label: 'Conversion Growth' },
    { value: '20+', label: 'Videos Produced Monthly' }
  ];

  const brandCategories = [
    { category: 'Fashion & Streetwear', brands: ['Jiberish', 'Stone Island', 'Homme Issey Miyake', 'Human Made', 'Kappa', 'PULLIN'] },
    { category: 'Sports & Outdoor', brands: ['Breezy Golf', 'Bob Does Sports', 'Van Deer', 'Skullcandy', 'Picture Organic'] },
    { category: 'F&B & Enterprise', brands: ["Arby's", 'Bank of America', 'Fireball Whisky', 'Grant Horvat'] },
    { category: 'Retail & Lifestyle', brands: ['Fusion Specialties', 'Oliver Mens Shop', 'YY Nation', 'Puppet Theater'] }
  ];

  const capabilities = [
    { title: 'Social Strategy & Growth', desc: 'End-to-end management of organic social accounts. Strategy, planning, publishing, and performance optimization.' },
    { title: 'Platform-Native Content', desc: 'Short-form video built for TikTok, Instagram, and YouTube. Not repurposed—designed for each platform from the start.' },
    { title: 'Campaign & GTM Execution', desc: 'Go-to-market campaigns across social, web, and email. Aligning content with product launches and business goals.' },
    { title: 'Influencer & Athlete Partnerships', desc: 'Outreach, contracts, and ongoing collaboration with influencers and athletes to create authentic, community-driven content.' },
    { title: 'Performance Analytics', desc: 'Translating engagement, reach, and conversion data into actionable insights. Iterating creative based on real-time results.' },
    { title: 'Cross-Functional Collaboration', desc: 'Working across creative, paid, sales, and product teams to ensure cohesive campaign execution.' }
  ];

  return (
    <section id="marketing" data-testid="marketing-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <AnimatedSection>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">Marketing Background</p>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight mb-6">
            Built to<br />Perform
          </h2>
          <p className="max-w-3xl text-zinc-400 text-lg leading-relaxed mb-20">
            10+ years producing content that drives real results for brands across outdoor, fashion, sports, and lifestyle. Not just making things look good—making them work.
          </p>
        </AnimatedSection>

        {/* Stats Row */}
        <AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24 pb-24 border-b border-white/10">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center md:text-left">
                <p className="font-display text-4xl md:text-5xl text-[#FFB703] mb-2">{stat.value}</p>
                <p className="font-mono text-xs uppercase tracking-wider text-zinc-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Capabilities Grid */}
        <AnimatedSection>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-8">Core Capabilities</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 mb-24 pb-24 border-b border-white/10">
            {capabilities.map((cap) => (
              <div key={cap.title}>
                <h3 className="text-white font-semibold text-lg mb-2">{cap.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Brand Roster */}
        <AnimatedSection>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-8">Brands by Category</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24 pb-24 border-b border-white/10">
            {brandCategories.map((cat) => (
              <div key={cat.category}>
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{cat.category}</h3>
                <ul className="space-y-2">
                  {cat.brands.map((brand) => (
                    <li key={brand} className="text-zinc-500 text-sm">{brand}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Experience Timeline */}
        <AnimatedSection>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-8">Experience</p>
          <div className="space-y-8">
            {[
              { role: 'Multimedia Producer', company: 'Fusion Specialties', period: '2025 — Present', desc: 'Lead content creation across social, web, and marketing channels. Own social-first content from concept through delivery.' },
              { role: 'Marketing & Digital Asset Creator', company: 'Global Sales Guys', period: '2023 — 2025', desc: 'Led social content strategy for outdoor and lifestyle brands. Supported international brands entering the U.S. market. Increased engagement by 30%.' },
              { role: 'Marketing & E-commerce Manager', company: 'Steadbrook LLC', period: '2022 — 2023', desc: 'Built and executed social and content strategies. Drove a 20% increase in e-commerce conversions through performance-driven content.' },
              { role: 'Asst. Creative Director', company: 'Jiberish Clothing', period: '2020 — 2022', desc: 'Led social media strategy and content direction. Managed influencer and athlete partnerships. Produced 20+ short-form videos monthly.' }
            ].map((job) => (
              <div key={job.company} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start group">
                <div className="md:col-span-3">
                  <p className="font-mono text-xs text-zinc-600 uppercase tracking-wider">{job.period}</p>
                </div>
                <div className="md:col-span-9">
                  <h3 className="text-white font-semibold">{job.role} <span className="text-[#FFB703]">@ {job.company}</span></h3>
                  <p className="text-zinc-500 text-sm mt-1 leading-relaxed">{job.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const testimonials = [
    {
      id: 1,
      quote: "Jack's work exceeded our expectations. He brought a cinematic quality to our brand that we didn't know was possible.",
      name: "Sarah Chen",
      role: "Marketing Director, Jiberish",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200"
    },
    {
      id: 2,
      quote: "The storytelling approach Jack brings to every project is unmatched. Our engagement tripled after his content strategy.",
      name: "Marcus Webb",
      role: "Founder, Breezy Golf",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200"
    },
    {
      id: 3,
      quote: "Working with Jack felt like a true creative partnership. He understood our vision and elevated it beyond what we imagined.",
      name: "Elena Rodriguez",
      role: "Brand Manager, Picture Organic",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200"
    }
  ];

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="text-center mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">Client Praise</p>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
              Testimonials
            </h2>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <AnimatedSection key={testimonial.id}>
              <div 
                data-testid={`testimonial-${testimonial.id}`}
                className="testimonial-card p-8 border border-white/10 h-full flex flex-col"
              >
                <Quote size={32} className="text-[#FFB703] mb-6" />
                <p className="text-lg text-zinc-300 leading-relaxed mb-8 flex-grow">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <AnimatedSection className="md:col-span-5">
            <div className="relative">
              <img
                src="https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/441ef620-e6e2-41bd-9307-0c4dc9eb8a0f/Headshot_Jack_BW-6.jpg?format=1000w"
                alt="Jack Pepper"
                data-testid="about-image"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#FFB703]" />
            </div>
          </AnimatedSection>

          <AnimatedSection className="md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">About</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-8">
              Jack Pepper
            </h2>
            <div className="space-y-6 text-zinc-400 leading-relaxed">
              <p>
                I'm Jack Pepper, a Denver-based multimedia producer and content strategist with a background in Cinema Studies and Moving Image Arts from the University of Colorado Boulder. That foundation is where I locked in a story-first approach to visual work, shaped early on through ski films like Strictly Business and Most Gutter.
              </p>
              <p>
                Since then, I've built a career creating high-quality, story-driven content for brands that care about craft and expect results. I work across outdoor, fashion, sports, and lifestyle, partnering with teams including Jiberish, Van Deer, Arby's, Bank of America, Issey Miyake, Skullcandy, Stone Island, Kappa, Human Made, and more.
              </p>
              <p>
                I also specialize in social media marketing, producing social-first content rooted in the outdoor and active lifestyle space. That work ranges from high-speed editorial cuts to lo-fi, UGC-style vertical video built for real performance, not just aesthetics.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#contact"
                data-testid="about-cta"
                className="btn-shine px-8 py-4 bg-[#FFB703] text-black font-bold uppercase tracking-wider hover:bg-[#EAA015] transition-colors"
              >
                Get In Touch
              </a>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

const getSubmitLabel = (sending, submitted) => {
  if (sending) return 'Sending...';
  if (submitted) return 'Message Sent!';
  return 'Send Message';
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pep and Co TikTok Section
const PepAndCo = () => {
  return (
    <section data-testid="pepandco-section" className="py-24 md:py-32 bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start mb-16">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">Side Project</p>
              <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-6">
                Pep & Co
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Started an endurance sport focused TikTok to document my journey of running for a year straight, as well as give me insights into new trends, what it takes to post everyday, what content actually does well, and what the people are talking about.
              </p>
              <a
                href="https://www.tiktok.com/@pep.and.company"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pepandco-tiktok-link"
                className="inline-flex items-center gap-3 px-6 py-3 border border-white/20 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
                <span className="font-mono text-sm uppercase tracking-wider">@pep.and.company</span>
              </a>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { id: 1, url: 'https://www.tiktok.com/@pep.and.company/video/7570365723449576718', thumbnail: 'https://customer-assets.emergentagent.com/job_design-upgrade-169/artifacts/t4t48syf_IMG_2663.PNG' },
                { id: 2, url: 'https://www.tiktok.com/@pep.and.company/video/7550364660558499127', thumbnail: 'https://customer-assets.emergentagent.com/job_design-upgrade-169/artifacts/uf7ld6kp_IMG_2665.PNG' },
                { id: 3, url: 'https://www.tiktok.com/@pep.and.company/video/7446829606860016938', thumbnail: 'https://customer-assets.emergentagent.com/job_design-upgrade-169/artifacts/3lh9dj5n_IMG_2667.PNG' }
              ].map((video) => (
                <a
                  key={video.id}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 aspect-[9/16]">
                    <img src={video.thumbnail} alt="Pep and Co TikTok" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#FFB703]/80 transition-colors duration-300">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    }
    setSending(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <AnimatedSection>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">Let's Connect</p>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight mb-8">
              Get In<br />Touch
            </h2>
            <p className="text-zinc-400 text-lg mb-10 max-w-md">
              I welcome your feedback and inquiries. For questions or concerns, let's talk about your next project.
            </p>
            <div className="space-y-4">
              <a 
                href="mailto:jackpeppervisuals@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-zinc-300 hover:text-[#FFB703] transition-colors"
              >
                <Mail size={20} />
                <span>jackpeppervisuals@gmail.com</span>
              </a>
              <a 
                href="tel:+15416335033"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-zinc-300 hover:text-[#FFB703] transition-colors"
              >
                <Phone size={20} />
                <span>(541) 633-5033</span>
              </a>
            </div>
            <div className="flex gap-4 mt-10">
              <a 
                href="https://www.instagram.com/jackypep/?hl=en" 
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="p-3 border border-white/10 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.tiktok.com/@pep.and.company" 
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok"
                className="p-3 border border-white/10 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
              </a>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Name</label>
                <input
                  type="text"
                  data-testid="contact-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-4 text-white focus:border-[#FFB703] outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email</label>
                <input
                  type="email"
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-4 text-white focus:border-[#FFB703] outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Message</label>
                <textarea
                  data-testid="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 py-4 text-white focus:border-[#FFB703] outline-none transition-colors resize-none"
                  placeholder="Tell me about your project..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="btn-shine w-full py-4 bg-[#FFB703] text-black font-bold uppercase tracking-wider hover:bg-[#EAA015] transition-colors disabled:opacity-50"
              >
                {getSubmitLabel(sending, submitted)}
              </button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-display text-xl tracking-tight">JACK PEPPER VISUALS</p>
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} All Rights Reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <main>
        <Hero />
        <Projects />
        <BrandMarquee />
        <Services />
        <MarketingBackground />
        <About />
        <PepAndCo />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-black min-h-screen">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/project/:slug" element={<ProjectPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
